// Library Methods
import { useState, useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Slider from "@mui/material/Slider";
import {
  HeadphonesIcon,
  HeadphonesOffIcon,
  MusicIcon,
  MusicOffIcon,
  SpeechBubbleIcon,
  SpeechBubbleOffIcon,
  VolumeIcon,
} from "src/assets/icons";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Services
import {
  deviceEnableControllerBeep,
  deviceDisableControllerBeep,
  deviceEnableWarnings,
  deviceDisableWarnings,
  deviceEnableSubtitles,
  deviceDisableSubtitles,
  deviceUpdateVolume,
} from "src/services/signalR/api";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Styles
import { useTheme } from "@mui/material/styles";
import { getSurfaceTokens } from "src/styles/mui/muiStylesHelper";

// Types
import { DeviceType } from "src/services/devices/types";

type LiveUpdateSettingsType = {
  connection: HubConnection | null;
};

export const LiveUpdateSettings = ({ connection }: LiveUpdateSettingsType) => {
  const { t } = useTranslation();
  const { getToken } = useTokenAccess();

  const { device, isOnline, isVisualField, isEsterman } =
    useLiveUpdateContext();

  const theme = useTheme();
  const { onSurface } = getSurfaceTokens(theme);

  const [deviceStatusVolume, setDeviceStatusVolume] = useState(
    device?.deviceStatus?.volume && !isNaN(device?.deviceStatus?.volume)
      ? device?.deviceStatus?.volume
      : 0
  );

  // Keep deviceStatusVolume in sync with the device's actual volume via SignalR push
  useEffect(() => {
    const handleDeviceListUpdated = (data: string) => {
      const parsedData = JSON.parse(data);
      const matchingDevice = parsedData?.data?.find(
        (item: DeviceType) => item.id === device?.id
      );

      if (matchingDevice) {
        setDeviceStatusVolume(matchingDevice?.deviceStatus?.volume ?? 0);
      }
    };

    connection?.on("DeviceListUpdated", handleDeviceListUpdated);

    return () => {
      connection?.off("DeviceListUpdated", handleDeviceListUpdated);
    };
  }, [connection, device?.id]);

  const intraExamProperties = device?.deviceStatus?.intraExamProperties;

  const [volume, setVolume] = useState<number>(
    device?.deviceStatus?.volume && !isNaN(device.deviceStatus.volume)
      ? Math.round(device.deviceStatus.volume * 100)
      : 0
  );

  // Ref so that changing it never triggers the device sync effect
  const isDraggingRef = useRef(false);

  const [shouldUpdateButtons, setShouldUpdateButtons] = useState(false);
  const [isBeepEnabled, setIsBeepEnabled] = useState(
    intraExamProperties?.visualFieldControllerBeepsEnabled
  );
  const [isWarningsEnabled, setIsWarningsEnabled] = useState(
    intraExamProperties?.visualFieldWarningsEnabled
  );
  const [isSubtitlesEnabled, setIsSubtitlesEnabled] = useState(
    intraExamProperties?.subtitlesEnabled
  );

  useEffect(() => {
    if (!isOnline) {
      setShouldUpdateButtons(true);
    } else if (isOnline && shouldUpdateButtons) {
      setIsBeepEnabled(intraExamProperties?.visualFieldControllerBeepsEnabled);
      setIsWarningsEnabled(intraExamProperties?.visualFieldWarningsEnabled);
      setIsSubtitlesEnabled(intraExamProperties?.subtitlesEnabled);
      setShouldUpdateButtons(false);
    }
  }, [isOnline, intraExamProperties, shouldUpdateButtons]);

  // Constraint 1: VR volume always wins — only runs when the device pushes a new
  // value. Using a ref for isDragging means toggling it never triggers this effect,
  // so the slider cannot snap back to the old device value on release.
  useEffect(() => {
    if (!isDraggingRef.current) {
      setVolume(Math.round(deviceStatusVolume * 100));
    }
  }, [deviceStatusVolume]);

  // Constraint 2: block device sync only while the slider is being dragged
  const handleVolumeChange = useCallback((event: Event) => {
    isDraggingRef.current = true;
    setVolume(Number((event?.target as HTMLInputElement)?.value));
  }, []);

  // Constraint 3: keep isDraggingRef true through the whole async send so no
  // stale device push can overwrite the slider before the command lands.
  // finally always clears it so VR updates resume regardless of outcome.
  const handleVolumeSet = useCallback(async () => {
    if (!device?.id || !connection) {
      isDraggingRef.current = false;
      return;
    }
    try {
      const token = await getToken();
      const formattedVolume = parseFloat((volume / 100).toString()).toFixed(2);
      await deviceUpdateVolume(token, device?.id, formattedVolume, connection);
    } catch (e) {
      console.error(e);
    } finally {
      isDraggingRef.current = false;
    }
  }, [volume, device?.id, connection, getToken]);

  // Enable controller beep sound
  const handleEnableControllerBeep = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceEnableControllerBeep(token, device?.id, connection);
      setIsBeepEnabled(true);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  // Disable controller beep sound
  const handleDisableControllerBeep = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceDisableControllerBeep(token, device?.id, connection);
      setIsBeepEnabled(false);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  // Enable warnings
  const handleEnableWarnings = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceEnableWarnings(token, device?.id, connection);
      setIsWarningsEnabled(true);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  // Disable warnings
  const handleDisableWarnings = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceDisableWarnings(token, device?.id, connection);
      setIsWarningsEnabled(false);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  // Enable subtitles
  const handleEnableSubtitles = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceEnableSubtitles(token, device?.id, connection);
      setIsSubtitlesEnabled(true);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  // Disable subtitles
  const handleDisableSubtitles = useCallback(async () => {
    if (!device?.id || !connection) return;

    try {
      const token = await getToken();
      await deviceDisableSubtitles(token, device?.id, connection);
      setIsSubtitlesEnabled(false);
    } catch (e) {
      console.error(e);
    }
  }, [connection, device?.id, getToken]);

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      justifyContent="space-between"
      columnGap="8px"
      p="10px 16px"
      borderBottom="1px solid #5B6D7A40"
    >
      <Box
        display="flex"
        justifyContent="flex-end"
        alignItems="center"
        gap="16px"
      >
        <Box display="flex" alignItems="center" gap="8px">
          <Box
            borderRadius="100%"
            height="10px"
            width="10px"
            sx={{
              backgroundColor: isOnline ? "#22BE39" : "#435B6D7A",
            }}
          />
          <Typography fontSize="12px">
            {isOnline ? t("Online") : t("Offline")}
          </Typography>
        </Box>

        {(isVisualField || isEsterman) && (
          <Tooltip
            title={
              isBeepEnabled
                ? t("Disable Controller Beep")
                : t("Enable Controller Beep")
            }
          >
            <span>
              <IconButton
                aria-label="controller-with-sound-effect"
                disabled={!isOnline}
                sx={{
                  color: onSurface,
                  opacity: !isOnline ? 0.3 : 1,
                  p: 0,
                  maxHeight: "20px",
                  height: "20px",
                  width: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
                onClick={
                  isBeepEnabled
                    ? handleDisableControllerBeep
                    : handleEnableControllerBeep
                }
              >
                {isBeepEnabled ? (
                  <MusicIcon aria-label="sound-icon-on" />
                ) : (
                  <MusicOffIcon aria-label="sound-icon-off" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        {(isVisualField || isEsterman) && (
          <Tooltip
            title={
              isWarningsEnabled ? t("Disable Warnings") : t("Enable Warnings")
            }
          >
            <span>
              <IconButton
                aria-label="headset-warnings"
                onClick={
                  isWarningsEnabled
                    ? handleDisableWarnings
                    : handleEnableWarnings
                }
                disabled={!isOnline}
                sx={{
                  color: onSurface,
                  opacity: !isOnline ? 0.3 : 1,
                  p: 0,
                  maxHeight: "20px",
                  height: "20px",
                  width: "20px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {isWarningsEnabled ? (
                  <HeadphonesIcon aria-label="warnings-icon-on" />
                ) : (
                  <HeadphonesOffIcon aria-label="warnings-icon-off" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip
          title={
            isSubtitlesEnabled ? t("Disable Subtitles") : t("Enable Subtitles")
          }
        >
          <span>
            <IconButton
              aria-label="subtitles"
              onClick={
                isSubtitlesEnabled
                  ? handleDisableSubtitles
                  : handleEnableSubtitles
              }
              disabled={!isOnline}
              sx={{
                color: onSurface,
                opacity: !isOnline ? 0.3 : 1,
                p: 0,
                maxHeight: "20px",
                height: "20px",
                width: "20px",
                display: "flex",
                alignItems: "center",
              }}
            >
              {isSubtitlesEnabled ? (
                <SpeechBubbleIcon aria-label="subtitles-icon-on" />
              ) : (
                <SpeechBubbleOffIcon aria-label="subtitles-icon-off" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Box
        display="flex"
        alignItems="center"
        justifyContent={{ xs: "flex-start", sm: "flex-end" }}
        minWidth="180px"
      >
        <Box mr="14px" color={onSurface}>
          <VolumeIcon width="20px" height="20px" />
        </Box>
        <Slider
          data-testid="volume-slider"
          min={0}
          max={100}
          value={volume}
          onChange={handleVolumeChange}
          onChangeCommitted={handleVolumeSet}
          sx={{
            p: 0,
            height: "10px",
            "& .MuiSlider-thumb": {
              backgroundColor: "#FFF",
              boxShadow: "none",
              border: "3px solid #E2772E",
              "&::before": {
                boxShadow: "none",
              },
              "&:hover, &.Mui-active, &.Mui-focusVisible": {
                boxShadow: "none",
              },
              "&:hover::before, &.Mui-active::before, &.Mui-focusVisible::before":
                {
                  boxShadow: "none",
                },
            },
            "& .MuiSlider-track": {
              backgroundColor: "#E2772E",
              border: "none",
            },
            "& .MuiSlider-rail": {
              opacity: 1,
              backgroundColor: "#5B6D7A20",
              boxShadow: "none",
            },
          }}
        />
        <Typography
          data-testid="volume-label"
          fontSize="12px"
          style={{ width: "30px", marginLeft: "16px" }}
        >
          {volume}
        </Typography>
      </Box>
    </Box>
  );
};

export default LiveUpdateSettings;
